/**
 * ISO-3166-1 alpha-2, and nothing more than that.
 *
 * # This list is not a list of countries we serve
 *
 * It is the set of codes that *exist*. Which of them are open to onboarding is
 * decided by the backend's jurisdiction registry, is fail-closed (invariant 7),
 * and has **no customer-facing endpoint** — `admin_jurisdiction_read.go` is the
 * operator surface. So this app cannot know the answer and must not imply it.
 *
 * That is deliberate rather than a limitation. A client-side "available
 * countries" list is a licensing decision taken in a browser bundle: it would
 * be a copy of the registry, it would drift the first time an operator opened a
 * country, and a customer in a country we had just opened would be told to go
 * away by a stale JavaScript array. The backend refuses, the refusal is
 * classified, and this file supplies only the vocabulary to ask the question.
 *
 * `go-wallet-web#1` names the same trap in the design kit: it ships flag assets
 * well beyond the EEA, and "a flag asset existing must never make a country
 * selectable".
 *
 * # Names come from `Intl`, not from here
 *
 * A hardcoded name table would be ~250 English strings to translate, and
 * FR-20.1 requires EN + DE/FR/ES/IT with no hardcoded customer-facing strings.
 * `Intl.DisplayNames` already holds every translation the browser ships with,
 * so the app carries codes and the platform supplies the words.
 */

/**
 * Every currently-assigned ISO-3166-1 alpha-2 code.
 *
 * Unfiltered on purpose, including the uninhabited territories. Any filtering
 * here would be this app judging where a person may open a wallet, which is
 * precisely the decision that belongs to the registry.
 */
export const COUNTRY_CODES = [
  'AD', 'AE', 'AF', 'AG', 'AI', 'AL', 'AM', 'AO', 'AQ', 'AR', 'AS', 'AT', 'AU', 'AW', 'AX', 'AZ',
  'BA', 'BB', 'BD', 'BE', 'BF', 'BG', 'BH', 'BI', 'BJ', 'BL', 'BM', 'BN', 'BO', 'BQ', 'BR', 'BS',
  'BT', 'BV', 'BW', 'BY', 'BZ', 'CA', 'CC', 'CD', 'CF', 'CG', 'CH', 'CI', 'CK', 'CL', 'CM', 'CN',
  'CO', 'CR', 'CU', 'CV', 'CW', 'CX', 'CY', 'CZ', 'DE', 'DJ', 'DK', 'DM', 'DO', 'DZ', 'EC', 'EE',
  'EG', 'EH', 'ER', 'ES', 'ET', 'FI', 'FJ', 'FK', 'FM', 'FO', 'FR', 'GA', 'GB', 'GD', 'GE', 'GF',
  'GG', 'GH', 'GI', 'GL', 'GM', 'GN', 'GP', 'GQ', 'GR', 'GS', 'GT', 'GU', 'GW', 'GY', 'HK', 'HM',
  'HN', 'HR', 'HT', 'HU', 'ID', 'IE', 'IL', 'IM', 'IN', 'IO', 'IQ', 'IR', 'IS', 'IT', 'JE', 'JM',
  'JO', 'JP', 'KE', 'KG', 'KH', 'KI', 'KM', 'KN', 'KP', 'KR', 'KW', 'KY', 'KZ', 'LA', 'LB', 'LC',
  'LI', 'LK', 'LR', 'LS', 'LT', 'LU', 'LV', 'LY', 'MA', 'MC', 'MD', 'ME', 'MF', 'MG', 'MH', 'MK',
  'ML', 'MM', 'MN', 'MO', 'MP', 'MQ', 'MR', 'MS', 'MT', 'MU', 'MV', 'MW', 'MX', 'MY', 'MZ', 'NA',
  'NC', 'NE', 'NF', 'NG', 'NI', 'NL', 'NO', 'NP', 'NR', 'NU', 'NZ', 'OM', 'PA', 'PE', 'PF', 'PG',
  'PH', 'PK', 'PL', 'PM', 'PN', 'PR', 'PS', 'PT', 'PW', 'PY', 'QA', 'RE', 'RO', 'RS', 'RU', 'RW',
  'SA', 'SB', 'SC', 'SD', 'SE', 'SG', 'SH', 'SI', 'SJ', 'SK', 'SL', 'SM', 'SN', 'SO', 'SR', 'SS',
  'ST', 'SV', 'SX', 'SY', 'SZ', 'TC', 'TD', 'TF', 'TG', 'TH', 'TJ', 'TK', 'TL', 'TM', 'TN', 'TO',
  'TR', 'TT', 'TV', 'TW', 'TZ', 'UA', 'UG', 'UM', 'US', 'UY', 'UZ', 'VA', 'VC', 'VE', 'VG', 'VI',
  'VN', 'VU', 'WF', 'WS', 'YE', 'YT', 'ZA', 'ZM', 'ZW',
] as const

export type CountryCode = (typeof COUNTRY_CODES)[number]

export function isCountryCode(v: string): v is CountryCode {
  return (COUNTRY_CODES as readonly string[]).includes(v)
}

/**
 * The country's name in the caller's language, or the bare code.
 *
 * Falling back to the code rather than to an English name: a select option
 * reading `BQ` is honest about what we know, and one silently reading English
 * inside a French page is a localization bug that nothing will ever report.
 */
export function countryName(code: string, locale?: string): string {
  try {
    const names = displayNames(locale)
    return names?.of(code) ?? code
  } catch {
    return code
  }
}

let cached: { locale: string | undefined; names: Intl.DisplayNames | null } | null = null

function displayNames(locale: string | undefined): Intl.DisplayNames | null {
  if (cached !== null && cached.locale === locale) return cached.names
  let names: Intl.DisplayNames | null = null
  try {
    // `type: 'region'` is the ISO-3166 table. `fallback: 'code'` makes an
    // unknown code return itself instead of undefined.
    names = new Intl.DisplayNames(locale === undefined ? undefined : [locale], {
      type: 'region',
      fallback: 'code',
    })
  } catch {
    // Some runtimes ship a trimmed ICU with no region data. Codes still work.
    names = null
  }
  cached = { locale, names }
  return names
}

/**
 * The codes, ordered by their name in the caller's language.
 *
 * Sorted with `Intl.Collator` rather than `<`, which would put `Ålesund` after
 * `Zimbabwe` in any Nordic locale and Åland with it.
 */
export function countriesByName(
  locale?: string,
): readonly { readonly code: CountryCode; readonly name: string }[] {
  const collator = new Intl.Collator(locale)
  return [...COUNTRY_CODES]
    .map((code) => ({ code, name: countryName(code, locale) }))
    .sort((a, b) => collator.compare(a.name, b.name))
}
